import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Close as CloseIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import {
  UnderstaffingSummary,
  StaffingTrendCard,
  AcuityGapCharts,
  PriorConduct,
  RelatedPartySection,
  OwnershipTimeline,
  OwnershipSnapshot,
  CaseActionSection,
} from '../components/understaffing';
import type { AcuityModel } from '../components/understaffing';
import CheckEngineDisplay, { SIGNAL_METADATA } from '../components/understaffing/CheckEngineDisplay';
import type { Signal } from '../components/understaffing/CheckEngineDisplay';
import CostReportAnalysisTab from '../components/understaffing/CostReportAnalysisTab';
import api from '../api/axios';

/* ───────────────── types ───────────────── */

interface OwnerRecord {
  owner_id: string;
  type_owner: string;
  owner_name: string;
  role_code_owner: string;
  role_text_owner: string;
  percentage_ownership: number | null;
  city_owner: string;
  state_owner: string;
  zip_code_owner: string;
  association_date_owner: string;
}

interface OtherFacilityOwner {
  owner_id: string;
  owner_name: string;
  percentage_ownership: number | null;
  role_text_owner: string;
}

interface OtherFacilityRecord {
  ccn: string;
  facility_name: string;
  dba_name?: string;
  state: string;
  in_understaffing_analysis: boolean;
  mds_cost: number | null;
  owners: OtherFacilityOwner[];
}

interface ChowEvent {
  effective_date: string;
  seller_name: string;
  buyer_name: string;
  chow_type: string;
  chow_type_text: string;
}

interface PeriodOwner {
  owner_name: string;
  type: string;
  percentage: number | null;
  role?: string;
}

interface OwnershipPeriod {
  enrollment_id: string;
  facility_name: string;
  start_month: string;
  end_month: string;
  is_current: boolean;
  direct_owners: PeriodOwner[];
  indirect_owners: PeriodOwner[];
  managerial_control: PeriodOwner[];
}

interface RPEntity {
  entity_name: string;
  common_owner: string | null;
  service_types: string[];
  address?: string;
  city_state_zip?: string;
  tags?: string[];
  in_latest_report?: boolean;
  years?: number[];
}

interface CoreData {
  facility: any;
  facility_years: any[];
  facility_years_hybrid?: any[];
  state_context?: any;
  state_acuity_by_year?: Record<number, any>;
  turnover_benchmarks?: any;
  sweetwater_baseline?: ProsecutionBaseline;
  nj_baseline?: ProsecutionBaseline;
  citation_benchmarks?: any;
  baselines_yearly?: {
    sweetwater: Record<string, number>;
    nj: Record<string, number>;
  };
  cost_reports?: { rpt_rec_num: number; fy_bgn_dt: string; fy_end_dt: string }[];
}

interface OwnerGroupRecord {
  group_id: string;
  owner_name: string;
  projected_understaffing: number | null;
  state: string;
}

interface OwnershipData {
  owners: OwnerRecord[];
  other_facilities: OtherFacilityRecord[];
  owner_groups?: OwnerGroupRecord[];
  file_month?: string;
  org_name?: string;
  dba_name?: string;
  chow_events?: ChowEvent[];
  ownership_periods?: OwnershipPeriod[];
}

interface RPData {
  rp_entities?: RPEntity[];
  rp_benchmarks?: any;
}

interface ProsecutionBaseline {
  facilities: number;
  nursing_hrs_per_patient_day: number | null;
  pct_days_below_cms_hprd: number | null;
  total_citations_per_100: number | null;
  harm_per_100_residents: number | null;
  avg_overall_rating: number | null;
  avg_staffing_rating: number | null;
  understaffed_state_min_hprd_hrs_per_res_day: number | null;
  ant_pct_acuity_staffed_total: number | null;
  ant_pct_acuity_staffed_rn: number | null;
  ant_pct_acuity_staffed_lpn: number | null;
  ant_pct_acuity_staffed_cna: number | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

/* ───────────────── helpers ───────────────── */


function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`facility-tabpanel-${index}`}
      aria-labelledby={`facility-tab-${index}`}
      style={{ display: value === index ? 'block' : 'none' }}
      {...other}
    >
      <Box sx={{ py: 3 }}>
        {children}
      </Box>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Related Parties sub-tabs
// ─────────────────────────────────────────────────────────────────────────────

const RelatedPartiesSubTabs: React.FC<{
  facility: any;
  facilityYears: any[];
  rpLoading: boolean;
  rpData: any;
  onGenerateCostReports: () => void;
  costReportGenerating: boolean;
  costReportProgress: number;
  costReportStatus: string;
  costReports?: any[];
  schemeId?: string;
  checkEngineSignals?: Signal[];
  checkEngineYears?: number[];
  checkEngineLoading?: boolean;
}> = ({
  facility,
  facilityYears,
  rpLoading,
  rpData,
  onGenerateCostReports,
  costReportGenerating,
  costReportProgress,
  costReportStatus,
  costReports,
  schemeId,
  checkEngineSignals,
  checkEngineYears,
  checkEngineLoading,
}) => {
  const [subTab, setSubTab] = React.useState(0);

  return (
    <Box>
      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{
          mb: 2,
          minHeight: 36,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTabs-indicator': { height: 2 },
          '& .MuiTab-root': {
            minHeight: 36,
            fontSize: '0.85rem',
            fontWeight: 500,
            textTransform: 'none',
            px: 2,
          },
        }}
      >
        <Tab label="Summary" />
        <Tab label="Related Party Payments" />
        <Tab label="Additional Related Party Analysis" />
        <Tab label="Other Flagged Transactions" />
        <Tab label="Cost Report Analysis" />
      </Tabs>

      {/* Summary */}
      {subTab === 0 && (
        rpLoading && !rpData ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress size={32} />
          </Box>
        ) : (
          <RelatedPartySection
            facility={facility}
            facilityYears={facilityYears}
            rpBenchmarks={rpData?.rp_benchmarks}
            rpEntities={rpData?.rp_entities}
            onGenerateCostReports={onGenerateCostReports}
            costReportGenerating={costReportGenerating}
            costReportProgress={costReportProgress}
            costReportStatus={costReportStatus}
            costReports={costReports}
            schemeId={schemeId}
          />
        )
      )}

      {/* Cost Centers */}
      {subTab === 1 && (
        <CostCentersTab
          facility={facility}
          facilityYears={facilityYears}
          schemeId={schemeId}
        />
      )}

      {/* Potential Non-Disclosed Related Parties — Check Engine Signals */}
      {subTab === 2 && (
        <CheckEngineDisplay
          signals={checkEngineSignals || []}
          years={checkEngineYears || []}
          loading={checkEngineLoading}
          categories={['Declared RP', 'Hidden Related Party']}
        />
      )}

      {/* Other Flagged Transactions */}
      {subTab === 3 && (
        <CheckEngineDisplay
          signals={checkEngineSignals || []}
          years={checkEngineYears || []}
          loading={checkEngineLoading}
          categories={['Staffing Integrity', 'Billing & Revenue', 'Balance Sheet YoY']}
          sections={[
            { id: 'A', label: 'Section A', subtitle: 'Staffing Flags', categories: ['Staffing Integrity'], color: '#00695c' },
            { id: 'B', label: 'Section B', subtitle: 'Billing & Revenue Flags', categories: ['Billing & Revenue'], color: '#1565c0' },
            { id: 'C', label: 'Section C', subtitle: 'Balance Sheet Flags', categories: ['Balance Sheet YoY'], color: '#e65100' },
          ]}
        />
      )}

      {/* Cost Report Analysis — per cost center vs all facilities */}
      {subTab === 4 && (
        <CostReportAnalysisTab
          ccn={facility?.ccn}
          state={facility?.state}
          schemeId={schemeId}
        />
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Cost Centers Tab
// ─────────────────────────────────────────────────────────────────────────────

const ccFmtMoney = (v: number | null | undefined) => {
  if (v == null || v === 0) return '-';
  const absVal = Math.abs(v);
  if (absVal >= 1000000) return `$${(absVal / 1000000).toFixed(1)}M`;
  if (absVal >= 1000) return `$${Math.round(absVal / 1000).toLocaleString()}K`;
  return `$${Math.round(absVal).toLocaleString()}`;
};

interface CostCenterDef {
  id: string;
  costCenter: string;
  label: string;
  paidField: string;
  allowableField: string;
  adjField: string | null;
  pctRevField: string | null;
  // Benchmark fields
  benchmarkPerBedField: string | null;
  benchmarkNField: string | null;
  // Interest sub-fields
  interestPaidField: string | null;
  interestAllowableField: string | null;
  interestAdjField: string | null;
}

interface CostCenterYearDef {
  id: string;
  benchmarkKey: string; // e.g. 'cc1' — used to look up median_cc1_per_bed, n_with_cc1
  costCenter: string;
  label: string;
  paidField: string;
  allowableField: string;
  adjField: string | null;
  pctRevField: string | null;
  interestPaidField: string | null;
  interestAllowableField: string | null;
  interestAdjField: string | null;
}

const COST_CENTER_DEFS: CostCenterYearDef[] = [
  // General Service Cost Centers
  { id: 'cc1', benchmarkKey: 'cc1', costCenter: 'Cost Center 1', label: 'Capital — Buildings & Fixtures',
    paidField: 'rp_real_estate_paid', allowableField: 'rp_real_estate', adjField: 'rp_real_estate_adj',
    pctRevField: 'rp_real_estate_paid_pct_gross_rev',
    interestPaidField: 'rp_interest_re_paid', interestAllowableField: 'rp_interest_re_allowable', interestAdjField: 'rp_interest_re_adj' },
  { id: 'cc2', benchmarkKey: 'cc2', costCenter: 'Cost Center 2', label: 'Capital — Moveable Equipment',
    paidField: 'rp_equipment_paid', allowableField: 'rp_equipment', adjField: 'rp_equipment_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc3', benchmarkKey: 'cc3', costCenter: 'Cost Center 3', label: 'Employee Benefits',
    paidField: 'rp_benefits_paid', allowableField: 'rp_benefits', adjField: 'rp_benefits_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc4', benchmarkKey: 'cc4', costCenter: 'Cost Center 4', label: 'Administrative & General',
    paidField: 'rp_mgmt_admin_paid', allowableField: 'rp_mgmt_admin', adjField: 'rp_mgmt_admin_adj',
    pctRevField: 'rp_mgmt_pct_gross_rev',
    interestPaidField: 'rp_interest_admin_paid', interestAllowableField: 'rp_interest_admin_allowable', interestAdjField: 'rp_interest_admin_adj' },
  { id: 'cc5', benchmarkKey: 'cc5', costCenter: 'Cost Center 5', label: 'Plant Operation, Maintenance & Repairs',
    paidField: 'rp_plant_ops_paid', allowableField: 'rp_plant_ops', adjField: 'rp_plant_ops_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc6', benchmarkKey: 'cc6', costCenter: 'Cost Center 6', label: 'Laundry & Linen Service',
    paidField: 'rp_laundry_paid', allowableField: 'rp_laundry', adjField: 'rp_laundry_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc7', benchmarkKey: 'cc7', costCenter: 'Cost Center 7', label: 'Housekeeping',
    paidField: 'rp_housekeeping_paid', allowableField: 'rp_housekeeping', adjField: 'rp_housekeeping_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc8', benchmarkKey: 'cc8', costCenter: 'Cost Center 8', label: 'Dietary',
    paidField: 'rp_dietary_paid', allowableField: 'rp_dietary', adjField: 'rp_dietary_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc9', benchmarkKey: 'cc9', costCenter: 'Cost Center 9', label: 'Nursing Administration',
    paidField: 'rp_nursing_admin_paid', allowableField: 'rp_nursing_admin', adjField: 'rp_nursing_admin_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc10', benchmarkKey: 'cc10', costCenter: 'Cost Center 10', label: 'Central Services & Supply',
    paidField: 'rp_central_svcs_paid', allowableField: 'rp_central_svcs', adjField: 'rp_central_svcs_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc11', benchmarkKey: 'cc11', costCenter: 'Cost Center 11', label: 'Pharmacy',
    paidField: 'rp_pharmacy_paid', allowableField: 'rp_pharmacy', adjField: 'rp_pharmacy_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc12', benchmarkKey: 'cc12', costCenter: 'Cost Center 12', label: 'Medical Records & Library',
    paidField: 'rp_medical_records_paid', allowableField: 'rp_medical_records', adjField: 'rp_medical_records_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc13', benchmarkKey: 'cc13', costCenter: 'Cost Center 13', label: 'Social Service',
    paidField: 'rp_social_service_paid', allowableField: 'rp_social_service', adjField: 'rp_social_service_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc14', benchmarkKey: 'cc14', costCenter: 'Cost Center 14', label: 'Nursing & Allied Health Education',
    paidField: 'rp_education_paid', allowableField: 'rp_education', adjField: 'rp_education_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  // Inpatient Routine Service Cost Centers
  { id: 'cc30', benchmarkKey: 'cc30', costCenter: 'Cost Center 30', label: 'Skilled Nursing Facility',
    paidField: 'rp_direct_nursing_paid', allowableField: 'rp_direct_nursing', adjField: 'rp_direct_nursing_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc31', benchmarkKey: 'cc31', costCenter: 'Cost Center 31', label: 'Nursing Facility',
    paidField: 'rp_nursing_facility_paid', allowableField: 'rp_nursing_facility', adjField: 'rp_nursing_facility_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  // Ancillary Service Cost Centers
  { id: 'cc44', benchmarkKey: 'cc44', costCenter: 'Cost Center 44', label: 'Physical Therapy',
    paidField: 'rp_physical_therapy_paid', allowableField: 'rp_physical_therapy', adjField: 'rp_physical_therapy_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc45', benchmarkKey: 'cc45', costCenter: 'Cost Center 45', label: 'Occupational Therapy',
    paidField: 'rp_occupational_therapy_paid', allowableField: 'rp_occupational_therapy', adjField: 'rp_occupational_therapy_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  { id: 'cc46', benchmarkKey: 'cc46', costCenter: 'Cost Center 46', label: 'Speech Pathology',
    paidField: 'rp_speech_pathology_paid', allowableField: 'rp_speech_pathology', adjField: 'rp_speech_pathology_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
  // Other
  { id: 'cc_other', benchmarkKey: '', costCenter: 'Other', label: 'Other Cost Centers',
    paidField: 'rp_other_paid', allowableField: 'rp_other', adjField: 'rp_other_adj',
    pctRevField: null, interestPaidField: null, interestAllowableField: null, interestAdjField: null },
];

function sumFromYears(facilityYears: any[], field: string): number {
  return facilityYears.reduce((total, fy) => {
    const m = fy.metadata || fy;
    return total + (m[field] ?? 0);
  }, 0);
}

// Map each legacy RP cost center (id 'cc<line>') to its Cost-Report-Analysis key
// by Worksheet-A line number. The card's LEFT side shows related-party payments
// (vs other RP facilities); its RIGHT side shows this center's TOTAL cost per
// resident-day vs ALL facilities — sourced from the cost-report-analysis endpoint.
const LINE_TO_CRA_KEY: Record<number, string> = {
  1: 'rent', 2: 'equipment', 3: 'benefits', 4: 'admin', 5: 'plant_ops',
  6: 'laundry', 7: 'housekeeping', 8: 'dietary', 9: 'nursing_admin', 10: 'central_svcs',
  11: 'pharmacy', 12: 'medical_records', 13: 'social_service', 14: 'education', 15: 'other_general',
  30: 'snf', 31: 'nf', 33: 'other_ltc',
  40: 'radiology', 41: 'lab', 42: 'iv_therapy', 43: 'oxygen', 44: 'pt',
  45: 'ot', 46: 'sp', 48: 'med_supplies', 49: 'drugs', 51: 'support_surfaces', 52: 'other_ancillary',
};

const CRA_STATUS_COLOR: Record<string, string> = { red: '#c62828', yellow: '#ef6c00', green: '#2e7d32' };

function ordinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}
const pctlLabel = (p: number) => `${ordinalSuffix(Math.min(99, Math.round(p)))} percentile`;

const CostCenterCard: React.FC<{
  def: CostCenterYearDef;
  facility: any;
  facilityYears: any[];
  craByKey: Record<string, { cell: any; year: number }>;
}> = ({ def, facility, facilityYears, craByKey }) => {
  const paid = sumFromYears(facilityYears, def.paidField) || sumFromYears(facilityYears, def.allowableField);
  const allowable = sumFromYears(facilityYears, def.allowableField);
  const adj = def.adjField ? sumFromYears(facilityYears, def.adjField) : null;
  const hasExcess = adj != null && adj < 0;

  // Right-side comparison: this center's TOTAL cost per resident-day vs ALL
  // facilities (from the cost-report-analysis data), mapped by Worksheet-A line.
  // Distinct from the related-party payments shown on the left.
  const craKey = def.benchmarkKey ? LINE_TO_CRA_KEY[parseInt(def.benchmarkKey.slice(2), 10)] : undefined;
  const craCell = craKey ? craByKey[craKey]?.cell : undefined;
  const craYear = craKey ? craByKey[craKey]?.year : undefined;

  // Interest
  const interestPaid = def.interestPaidField ? sumFromYears(facilityYears, def.interestPaidField) : null;
  const interestAllowable = def.interestAllowableField ? sumFromYears(facilityYears, def.interestAllowableField) : null;
  const interestAdj = def.interestAdjField ? sumFromYears(facilityYears, def.interestAdjField) : null;

  // % of total RP
  const totalRpPaid = sumFromYears(facilityYears, 'rp_total_paid') || sumFromYears(facilityYears, 'rp_total') || 0;
  const pctOfTotal = totalRpPaid > 0 && paid ? (paid / totalRpPaid) * 100 : null;

  if (!paid || paid <= 0) return null;

  return (
    <Box sx={{ p: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', gap: 4 }}>
        {/* Left: Header + metrics grouped together */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Header */}
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>{def.label}</Typography>
          </Box>

          {/* Metrics row — each term has a hover definition (dotted underline), worded to CMS sources */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Tooltip arrow title="Amount the provider included in its Worksheet A costs for services, facilities, or supplies furnished by a related organization — i.e., what it paid the related party. Source: Form CMS-2540-10, Wksht A-8-1, col. 5 ('Amount Included in Wksht. A').">
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'inline-block', cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}>Paid to RP</Typography>
              </Tooltip>
              <Typography variant="h6" fontWeight={700}>{ccFmtMoney(paid)}</Typography>
            </Box>
            <Box>
              <Tooltip arrow title="The related organization's allowable cost. Under 42 CFR 413.17, related-party costs are allowable only at the cost TO the related organization — not the price it charged. Source: Form CMS-2540-10, Wksht A-8-1, col. 4 ('Amount Allowable in Cost'); 42 CFR 413.17.">
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'inline-block', cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}>CMS Allowable</Typography>
              </Tooltip>
              <Typography variant="h6" fontWeight={700} color="text.secondary">{ccFmtMoney(allowable)}</Typography>
            </Box>
            {hasExcess && (
              <Box>
                <Tooltip arrow title="Worksheet A-8-1, col. 6 — the adjustment (col. 4 allowable − col. 5 included). When the provider paid the related party more than its allowable cost, this removes the excess from reimbursable cost; shown here as its magnitude. Source: Form CMS-2540-10, Wksht A-8-1; 42 CFR 413.17.">
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'inline-block', cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}>Adjustment</Typography>
                </Tooltip>
                <Typography variant="h6" fontWeight={700} color="error.main">{ccFmtMoney(Math.abs(adj!))}</Typography>
              </Box>
            )}
            {interestPaid != null && interestPaid > 0 && (
              <Box>
                <Tooltip arrow title="Interest paid to a related organization (e.g., on a related-party loan or mortgage), subject to the same 42 CFR 413.17 related-organization principle.">
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'inline-block', cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}>RP Interest</Typography>
                </Tooltip>
                <Typography variant="h6" fontWeight={700}>{ccFmtMoney(interestPaid)}</Typography>
              </Box>
            )}
          </Box>

          {/* Plain-English summary so the numbers read as one story instead of floating */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontSize: '0.78rem', lineHeight: 1.45 }}>
            {hasExcess ? (
              <>Paid <strong>{ccFmtMoney(paid)}</strong> to a related organization; under 42 CFR 413.17 only <strong>{ccFmtMoney(allowable)}</strong> (the related party&apos;s cost) is allowable, so Worksheet A-8-1 adjusts out <strong style={{ color: '#d32f2f' }}>{ccFmtMoney(Math.abs(adj!))}</strong> ({(((paid - allowable) / paid) * 100).toFixed(0)}% of what was paid).</>
            ) : (
              <>Paid <strong>{ccFmtMoney(paid)}</strong> to a related organization, fully within its allowable cost under 42 CFR 413.17 — no adjustment.</>
            )}
          </Typography>
        </Box>

        {/* Right: TOTAL cost-center cost per resident-day vs ALL facilities —
            a different question than the related-party payments on the left. */}
        {craKey && (
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {/* Callout: what's being compared here (vs the RP payments on the left) */}
            <Box sx={{ mb: 1 }}>
              <Tooltip arrow title="Independent of the related-party payments on the left. This compares the facility's TOTAL cost for this cost center (every dollar on Worksheet A — not only what was paid to related parties) per resident-day against ALL facilities in its segment, related-party or not. A high total cost is a flag whether or not a related party is disclosed.">
                <Typography variant="caption" fontWeight={700} sx={{ display: 'inline-block', textTransform: 'uppercase', fontSize: '0.6rem', cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled' }}>
                  Total cost per resident-day
                </Typography>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem', mt: 0.25 }}>
                vs all {craCell?.segment || facility.state} facilities{craYear ? ` · FY ${craYear}` : ''}
              </Typography>
            </Box>

            {craCell?.facility_value != null ? (() => {
              const dist = craCell.distribution?.all || {};
              const facVal: number = craCell.facility_value;
              const median: number | null = dist.median ?? null;
              const p70: number | null = dist.p70 ?? null;
              const p90: number | null = dist.p90 ?? null;
              const pct: number | null = craCell.percentiles?.all ?? null;
              const sc = craCell.status ? CRA_STATUS_COLOR[craCell.status] : '#607d8b';
              const maxBar = (Math.max(facVal, median ?? 0, p90 ?? 0) * 1.1) || 1;
              const seg = craCell.segment || `${facility.state}`;
              const bars: { label: string; value: number; color: string; hl: boolean }[] = [
                { label: 'This Facility', value: facVal, color: sc, hl: true },
                ...(median != null ? [{ label: `${seg} median`, value: median, color: '#90caf9', hl: false }] : []),
              ];
              return (
                <>
                  {pct != null && (
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1 }}>
                      <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: sc, lineHeight: 1 }}>{pctlLabel(pct)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        higher than {Math.min(99, Math.round(pct))}% of {seg} facilities
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {bars.map((item, idx) => (
                      <Box key={idx}>
                        <Box display="flex" justifyContent="space-between" mb={0.25}>
                          <Typography variant="caption" fontWeight={item.hl ? 700 : 500} sx={{ color: item.hl ? item.color : 'text.secondary' }}>{item.label}</Typography>
                          <Typography variant="caption" fontWeight={700} sx={{ color: item.hl ? item.color : 'text.secondary' }}>${item.value.toFixed(2)}/day</Typography>
                        </Box>
                        <Box sx={{ position: 'relative', height: item.hl ? 16 : 10, backgroundColor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${Math.min(100, (item.value / maxBar) * 100)}%`, backgroundColor: item.color, borderRadius: 1, transition: 'width 0.5s ease-out' }} />
                          {/* p70 / p90 zone boundaries drawn on the facility bar's track
                              (white halo keeps them visible even over a same-color fill) */}
                          {item.hl && p70 != null && <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(p70 / maxBar) * 100}%`, width: '2px', backgroundColor: '#ef6c00', boxShadow: '0 0 0 0.5px rgba(255,255,255,0.9)' }} />}
                          {item.hl && p90 != null && <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(p90 / maxBar) * 100}%`, width: '2px', backgroundColor: '#c62828', boxShadow: '0 0 0 0.5px rgba(255,255,255,0.9)' }} />}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  {(p70 != null || p90 != null) && (
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <Box sx={{ width: 2, height: 10, backgroundColor: '#ef6c00' }} /><Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>p70</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                        <Box sx={{ width: 2, height: 10, backgroundColor: '#c62828' }} /><Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>p90</Typography>
                      </Box>
                    </Box>
                  )}
                </>
              );
            })() : (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                No all-facility cost-report total for this center.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const CostCentersTab: React.FC<{
  facility: any;
  facilityYears: any[];
  schemeId: string | undefined;
}> = ({ facility, facilityYears, schemeId }) => {
  // Cost-report-analysis data powers the right side of each card: the center's
  // TOTAL cost per resident-day vs ALL facilities (same endpoint as the Cost
  // Report Analysis tab). Keyed by cost-center key → latest year with a value.
  const [cra, setCra] = useState<any>(null);
  useEffect(() => {
    if (!facility?.ccn || !schemeId) return;
    let active = true;
    const params = facility.state ? `?state=${encodeURIComponent(facility.state)}` : '';
    api.get(`/schemes/${schemeId}/understaffing/facilities/${facility.ccn}/cost-report-analysis/${params}`)
      .then((res) => { if (active) setCra(res.data); })
      .catch((err) => console.error('Failed to fetch cost report analysis', err));
    return () => { active = false; };
  }, [facility?.ccn, facility?.state, schemeId]);

  const craByKey = React.useMemo(() => {
    const out: Record<string, { cell: any; year: number }> = {};
    const years: number[] = cra?.years || [];
    for (const cc of (cra?.cost_centers || [])) {
      let picked: { cell: any; year: number } | null = null;
      for (const y of years) {                       // ascending → keep the latest with data
        const c = cc.years?.[String(y)];
        if (c?.facility_value != null) picked = { cell: c, year: y };
      }
      if (picked) out[cc.key] = picked;
    }
    return out;
  }, [cra]);

  const hasAnyData = COST_CENTER_DEFS.some(def => {
    const paid = sumFromYears(facilityYears, def.paidField) || sumFromYears(facilityYears, def.allowableField);
    return paid > 0;
  });

  if (!hasAnyData) {
    return (
      <Typography color="text.secondary">No cost center data available.</Typography>
    );
  }

  // Sort cost centers by Paid to RP descending
  const sortedDefs = [...COST_CENTER_DEFS]
    .filter(def => {
      const paid = sumFromYears(facilityYears, def.paidField) || sumFromYears(facilityYears, def.allowableField);
      return paid > 0;
    })
    .sort((a, b) => {
      const paidA = sumFromYears(facilityYears, a.paidField) || sumFromYears(facilityYears, a.allowableField);
      const paidB = sumFromYears(facilityYears, b.paidField) || sumFromYears(facilityYears, b.allowableField);
      return paidB - paidA;
    });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sortedDefs.map(def => (
        <CostCenterCard
          key={def.id}
          def={def}
          facility={facility}
          facilityYears={facilityYears}
          craByKey={craByKey}
        />
      ))}
    </Box>
  );
};

const tabMap: Record<string, number> = {
  understaffing: 0,
  conduct: 1,
  prior_conduct: 1,
  related_parties: 2,
  payments: 2, // legacy alias
  case_action: 3,
  ownership: 4,
};

const tabNames = ['understaffing', 'prior_conduct', 'related_parties', 'case_action', 'ownership'];

/* ───────────────── risk score modal helpers ───────────────── */

const RISK_COLORS: Record<string, string> = {
  critical: '#d32f2f',
  high: '#ed6c02',
  elevated: '#fbc02d',
  moderate: '#1976d2',
  low: '#2e7d32',
};

const RISK_FACTORS = [
  { key: 'factor1_score', label: 'Total Acuity Gap', weight: 20, pctKey: 'total_acuity_gap_pct', description: 'How far below acuity-based staffing needs the facility falls (all staff types).' },
  { key: 'factor2_score', label: '% Days Below Acuity', weight: 15, pctKey: 'avg_pct_days_below_acuity', description: 'Percentage of days the facility fails to staff to acuity needs.' },
  { key: 'factor3_score', label: 'Understaffing Cost', weight: 20, pctKey: 'risk_understaffing_cost', description: 'Projected total understaffing cost without overstaffing credit.' },
  { key: 'factor4_score', label: 'Staffing Citations', weight: 5, pctKey: 'total_staffing_citations', description: 'Understaffing and staffing-related survey citations.' },
  { key: 'factor5_score', label: 'Abuse Citations', weight: 5, pctKey: 'total_abuse_citations', description: 'Abuse and neglect-related survey citations.' },
  { key: 'factor6_score', label: 'RP Paid/Day', weight: 10, pctKey: 'rp_paid_per_resident_day', description: 'Total related party payments per resident per day.' },
  { key: 'factor7_score', label: 'RP % Revenue', weight: 10, pctKey: 'rp_pct_revenue', description: 'Related party payments as a percentage of gross revenue.' },
  { key: 'factor8_score', label: 'RP Flags', weight: 5, pctKey: 'rp_flag_score', description: 'Declared and hidden related party check engine flags.' },
  { key: 'factor9_score', label: 'Other Flags', weight: 5, pctKey: 'other_flag_score', description: 'Other flagged transactions (staffing integrity signals).' },
  { key: 'factor10_score', label: 'Star Rating', weight: 5, pctKey: 'star_rating_inverted', description: 'CMS star rating (lower stars = higher risk).' },
];



/* ───────────────── component ───────────────── */

export default function UnderstaffingFacilityPage() {
  const { ccn } = useParams<{ ccn: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation context from navigation state
  const chainCcns: string[] | undefined = location.state?.chainCcns;
  const chainName: string | undefined = location.state?.chainName;
  const fromState: string | undefined = location.state?.fromState;

  // Tab state synced to URL params
  const tabParam = searchParams.get('tab');
  const [tabValue, setTabValue] = useState(tabParam && tabMap[tabParam] !== undefined ? tabMap[tabParam] : 0);
  const [acuityModel, setAcuityModel] = useState<AcuityModel>('hybrid');
  const [riskScoreOpen, setRiskScoreOpen] = useState(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const newParams = new URLSearchParams(searchParams);
    if (newValue === 0) {
      newParams.delete('tab');
    } else {
      newParams.set('tab', tabNames[newValue]);
    }
    setSearchParams(newParams, { replace: true });
  };

  // Data state — split into 3 independent fetches
  const [schemeId, setSchemeId] = useState<string | null>(null);
  const [coreData, setCoreData] = useState<CoreData | null>(null);
  const [ownershipData, setOwnershipData] = useState<OwnershipData | null>(null);
  const [rpData, setRpData] = useState<RPData | null>(null);
  const [checkEngineData, setCheckEngineData] = useState<{ signals: Signal[]; years: number[] } | null>(null);
  const [coreLoading, setCoreLoading] = useState(true);
  const [ownershipLoading, setOwnershipLoading] = useState(false);
  const [rpLoading, setRpLoading] = useState(false);
  const [checkEngineLoading, setCheckEngineLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cost report generation state
  const [costReportGenerating, setCostReportGenerating] = useState(false);
  const [costReportProgress, setCostReportProgress] = useState<number>(0);
  const [costReportStatus, setCostReportStatus] = useState<string>('');


  // Fetch scheme ID on mount
  useEffect(() => {
    const fetchScheme = async () => {
      try {
        const res = await api.get('/schemes/');
        const schemes = res.data.results ?? res.data;
        const understaffingScheme = schemes.find((s: any) => s.code === 'snf-understaffing');
        if (understaffingScheme) {
          setSchemeId(understaffingScheme.id);
        } else {
          setError('SNF Understaffing scheme not found');
          setCoreLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch schemes', err);
        setError('Failed to load scheme data');
        setCoreLoading(false);
      }
    };
    fetchScheme();
  }, []);

  // Ownership fetch helper — pass state hint to skip redundant facility detail query
  const fetchOwnership = useCallback(async (stateHint?: string) => {
    if (!schemeId || !ccn) return;
    setOwnershipLoading(true);
    try {
      const params = stateHint ? `?state=${stateHint}` : '';
      const res = await api.get(`/schemes/${schemeId}/understaffing/facilities/${ccn}/ownership/${params}`);
      setOwnershipData(res.data);
    } catch (err) {
      console.error('Failed to fetch ownership data', err);
    } finally {
      setOwnershipLoading(false);
    }
  }, [schemeId, ccn]);

  // RP fetch helper
  const fetchRP = useCallback(async () => {
    if (!schemeId || !ccn) return;
    setRpLoading(true);
    try {
      const res = await api.get(`/schemes/${schemeId}/understaffing/facilities/${ccn}/related-parties/`);
      setRpData(res.data);
    } catch (err) {
      console.error('Failed to fetch related parties data', err);
    } finally {
      setRpLoading(false);
    }
  }, [schemeId, ccn]);

  // Check engine fetch helper
  const fetchCheckEngine = useCallback(async (stateHint?: string) => {
    if (!schemeId || !ccn) return;
    setCheckEngineLoading(true);
    try {
      const params = stateHint ? `?state=${stateHint}` : '';
      const res = await api.get(`/schemes/${schemeId}/understaffing/facilities/${ccn}/check-engine/${params}`);
      const apiSignals = (res.data.signals || []).filter((s: any) => s.signal_id !== 'cr_vs_pbj').map((s: any) => {
        const meta = SIGNAL_METADATA[s.signal_id] || { name: s.signal_id, description: '', hover: '', unit: '' };
        const years: Record<number, any> = {};
        if (s.years) {
          for (const [yr, data] of Object.entries(s.years)) {
            const d = data as any;
            years[Number(yr)] = {
              status: d.status || null,
              value: d.facility_value,
              threshold_median: d.threshold_median,
              threshold_yellow: d.threshold_yellow,
              threshold_red: d.threshold_red,
              detail: d.detail || undefined,
            };
          }
        }
        return {
          id: s.signal_id,
          name: meta.name,
          description: meta.description,
          hover: meta.hover,
          category: s.signal_id === 'contract_labor' ? 'Hidden Related Party' : s.category,
          isBinary: s.is_binary,
          unit: meta.unit,
          yellowLabel: meta.yellowLabel,
          redLabel: meta.redLabel,
          urbanRural: s.urban_rural,
          years,
        } as Signal;
      });
      setCheckEngineData({ signals: apiSignals, years: res.data.years || [] });
    } catch (err) {
      console.error('Failed to fetch check engine signals', err);
    } finally {
      setCheckEngineLoading(false);
    }
  }, [schemeId, ccn]);

  // Fetch core data + active tab's data in parallel
  useEffect(() => {
    if (!schemeId || !ccn) return;

    // Reset stale data from previous facility
    setCoreData(null);
    setOwnershipData(null);
    setRpData(null);
    setCheckEngineData(null);
    setCoreLoading(true);
    setError(null);

    const fetchCore = async () => {
      try {
        const res = await api.get(`/schemes/${schemeId}/understaffing/facilities/${ccn}/`);
        setCoreData(res.data);
      } catch (err: any) {
        console.error('Failed to fetch facility detail', err);
        if (err.response?.status === 404) {
          setError(`Facility with CCN ${ccn} not found`);
        } else {
          setError('Failed to load facility data');
        }
      } finally {
        setCoreLoading(false);
      }
    };

    fetchCore();

    // Fetch ownership eagerly — pass state hint from nav context to skip redundant DB lookup
    fetchOwnership(fromState);

    // Also fetch RP + check engine data if active tab needs it
    if (tabValue === 2) {
      fetchRP();
      fetchCheckEngine(fromState);
    }
  }, [schemeId, ccn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-fetch RP + check engine when switching to related_parties tab
  useEffect(() => {
    if (tabValue === 2 && schemeId && ccn) {
      if (!rpData && !rpLoading) fetchRP();
      if (!checkEngineData && !checkEngineLoading) fetchCheckEngine(fromState);
    }
  }, [tabValue, schemeId, ccn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cost report generation
  const handleGenerateCostReports = useCallback(async () => {
    if (!schemeId || !ccn) return;

    try {
      setCostReportGenerating(true);
      setCostReportProgress(5);
      setCostReportStatus('Starting cost report generation...');

      const response = await api.post(
        `/schemes/${schemeId}/generate-cost-reports/`,
        { ccn }
      );
      const taskId = response.data.task_id;

      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await api.get(
            `/schemes/${schemeId}/cost-reports-status/?task_id=${taskId}`
          );
          const { state, pct, msg, download_url } = statusResponse.data;

          if (pct) setCostReportProgress(pct);
          if (msg) setCostReportStatus(msg);

          if (state === 'SUCCESS') {
            clearInterval(pollInterval);
            setCostReportStatus('Download ready!');

            if (download_url) {
              try {
                // Request as blob to handle both S3 (JSON) and local (binary) responses
                const response = await api.get(download_url, { responseType: 'blob' });
                const contentType = response.headers['content-type'];

                if (contentType?.includes('application/json')) {
                  // S3 case: response is JSON with presigned URL
                  const text = await response.data.text();
                  const json = JSON.parse(text);
                  if (json.url) {
                    window.location.href = json.url;
                  }
                } else {
                  // Local storage case: response is the actual file
                  const blob = response.data;
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  const disposition = response.headers['content-disposition'];
                  const filenameMatch = disposition?.match(/filename="?(.+?)"?$/);
                  link.download = filenameMatch ? filenameMatch[1] : `cost_reports_${ccn}.zip`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                }
              } catch (fetchErr) {
                console.error('Failed to get download URL:', fetchErr);
              }
            }

            setTimeout(() => {
              setCostReportGenerating(false);
              setCostReportProgress(0);
              setCostReportStatus('');
            }, 2000);
          } else if (state === 'FAILURE') {
            clearInterval(pollInterval);
            setCostReportStatus('Failed to generate cost reports');
            setTimeout(() => {
              setCostReportGenerating(false);
              setCostReportProgress(0);
              setCostReportStatus('');
            }, 3000);
          }
        } catch (pollErr) {
          clearInterval(pollInterval);
          setCostReportGenerating(false);
          console.error('Polling error:', pollErr);
        }
      }, 2000);
    } catch (err) {
      console.error('Failed to start cost report generation', err);
      setCostReportGenerating(false);
      setCostReportProgress(0);
      setCostReportStatus('Failed to start');
    }
  }, [schemeId, ccn]);

  // Handle facility switch (for multi-facility chains)
  const handleFacilitySwitch = (newCcn: string) => {
    navigate(`/schemes/snf-understaffing/facility/${newCcn}`, {
      state: location.state,
      replace: true,
    });
  };

  // Build facility prop object from API response
  const buildFacilityProp = (facility: any) => {
    const mdsRnHrs = facility.metadata?.total_understaffed_mds_rn_hrs ?? 0;
    const mdsLpnHrs = facility.metadata?.total_understaffed_mds_lpn_hrs ?? 0;
    const mdsAideHrs = facility.metadata?.total_ant_understaffed_cna_hrs ?? 0;
    const totalMdsHrs = mdsRnHrs + mdsLpnHrs + mdsAideHrs;

    const stateMinHrs = facility.total_understaffed_state_min_hprd_hrs
      ?? facility.metadata?.total_understaffed_state_min_hprd_hrs;
    const stateMinCost = facility.total_understaffed_state_min_hprd_cost
      ?? facility.metadata?.total_understaffed_state_min_hprd_cost;

    return {
      ccn: facility.ccn,
      facility_name: facility.facility_name,
      state: facility.state,
      beds: facility.metadata?.beds,
      avg_census: facility.metadata?.avg_census,
      avg_cms_total_staffing_hrd: facility.metadata?.avg_cms_total_staffing_hrd,
      avg_pbj_nursing_hrd: facility.avg_pbj_nursing_hrd,
      total_understaffed_state_min_hprd_hrs: stateMinHrs,
      total_understaffed_state_min_hprd_cost: stateMinCost,
      pct_days_below_cms_hprd: facility.metadata?.pct_days_below_cms_hprd,
      understaffed_state_min_hprd_hrs_per_res_day: facility.metadata?.understaffed_state_min_hprd_hrs_per_res_day,
      total_understaffed_mds_hrs: totalMdsHrs > 0 ? totalMdsHrs : null,
      total_mds_understaffing_cost: facility.metadata?.total_mds_understaffing_cost
        ?? (facility.metadata?.total_understaffed_mds_rn_cost ?? 0)
        + (facility.metadata?.total_understaffed_mds_lpn_cost ?? 0)
        + (facility.metadata?.total_ant_understaffed_cna_cost ?? 0),
      // Citation metrics
      total_harm_citations: facility.total_harm_citations ?? facility.metadata?.total_harm_citations,
      total_jeopardy_citations: facility.metadata?.total_jeopardy_citations,
      harm_per_100_residents: facility.metadata?.harm_per_100_residents,
      total_citations_per_100: facility.metadata?.total_citations_per_100,
      total_citations: facility.metadata?.total_citations,
      total_fine_amount: facility.total_fine_amount ?? facility.metadata?.total_fine_amount,
      staffing_ftags_total: facility.metadata?.staffing_ftags_total,
      staffing_harm_total: facility.metadata?.staffing_harm_total,
      // F-tag category totals
      total_staffing_ftags: facility.metadata?.total_staffing_ftags,
      total_abuse_neglect_ftags: facility.metadata?.total_abuse_neglect_ftags,
      total_quality_of_care_ftags: facility.metadata?.total_quality_of_care_ftags,
      total_infection_control_ftags: facility.metadata?.total_infection_control_ftags,
      total_pharmacy_ftags: facility.metadata?.total_pharmacy_ftags,
      total_behavioral_health_ftags: facility.metadata?.total_behavioral_health_ftags,
      total_dietary_ftags: facility.metadata?.total_dietary_ftags,
      // Wage analysis
      rn_lpn_wage_diff: facility.metadata?.rn_lpn_wage_diff,
      avg_cr_rn_hourly_wage: facility.metadata?.avg_cr_rn_hourly_wage,
      avg_cr_lpn_hourly_wage: facility.metadata?.avg_cr_lpn_hourly_wage,
      avg_cr_cna_hourly_wage: facility.metadata?.avg_cr_cna_hourly_wage,
      narrow_rn_lpn_wage_diff: facility.metadata?.narrow_rn_lpn_wage_diff,
      // Related party (allowable)
      total_rp_total: facility.metadata?.total_rp_total,
      rp_total_per_resident_day: facility.rp_total_per_resident_day ?? facility.metadata?.rp_total_per_resident_day,
      rp_total_pct_gross_rev: facility.rp_total_pct_gross_rev ?? facility.metadata?.rp_total_pct_gross_rev,
      rp_mgmt_pct_gross_rev: facility.metadata?.rp_mgmt_pct_gross_rev,
      total_rp_real_estate: facility.total_rp_real_estate ?? facility.metadata?.total_rp_real_estate,
      admin_mgmt_pct_gross_rev: facility.metadata?.admin_mgmt_pct_gross_rev,
      // Related party (paid)
      total_rp_total_paid: facility.total_rp_total_paid ?? facility.metadata?.total_rp_total_paid,
      total_rp_total_adj: facility.total_rp_total_adj ?? facility.metadata?.total_rp_total_adj,
      rp_total_paid_per_resident_day: facility.rp_total_paid_per_resident_day ?? facility.metadata?.rp_total_paid_per_resident_day,
      rp_total_paid_pct_gross_rev: facility.rp_total_paid_pct_gross_rev ?? facility.metadata?.rp_total_paid_pct_gross_rev,
      total_rp_real_estate_paid: facility.total_rp_real_estate_paid ?? facility.metadata?.total_rp_real_estate_paid,
      total_rp_real_estate_adj: facility.metadata?.total_rp_real_estate_adj,
      // RP Admin/Management
      total_rp_mgmt_admin: facility.total_rp_mgmt_admin ?? facility.metadata?.total_rp_mgmt_admin,
      total_rp_mgmt_admin_paid: facility.total_rp_mgmt_admin_paid ?? facility.metadata?.total_rp_mgmt_admin_paid,
      total_rp_mgmt_admin_adj: facility.total_rp_mgmt_admin_adj ?? facility.metadata?.total_rp_mgmt_admin_adj,
      // B-I allocations
      total_cap_buildings: facility.total_cap_buildings ?? facility.metadata?.total_cap_buildings,
      total_cap_equipment: facility.total_cap_equipment ?? facility.metadata?.total_cap_equipment,
      total_admin_general: facility.total_admin_general ?? facility.metadata?.total_admin_general,
      // Nursing turnover
      avg_nursing_turnover: facility.avg_nursing_turnover ?? facility.metadata?.avg_nursing_turnover,
      // Wage source tracking
      years_with_cr_wages: facility.metadata?.years_with_cr_wages,
      num_years: facility.metadata?.num_years,
    };
  };

  // Loading state — wait for core data
  if (coreLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  // Error state
  const fallbackBackUrl = fromState ? `/schemes/snf-understaffing/state/${fromState}` : '/schemes/snf-understaffing';
  const fallbackBackLabel = fromState ? `Back to ${fromState}` : 'Back to Understaffing';

  if (error || !coreData) {
    return (
      <Layout>
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(fallbackBackUrl)}
            sx={{ mb: 2 }}
          >
            {fallbackBackLabel}
          </Button>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error" variant="h6">
              {error || 'Failed to load facility data'}
            </Typography>
          </Paper>
        </Box>
      </Layout>
    );
  }

  const facility = coreData.facility;
  const facilityProp = buildFacilityProp(facility);
  const facilityYears = coreData.facility_years || [];
  const facilityYearsHybrid = coreData.facility_years_hybrid || [];

  const facilityBackState = fromState || facility.state;
  const backUrl = `/schemes/snf-understaffing/state/${facilityBackState}`;
  const backLabel = `Back to ${facilityBackState}`;

  // Prosecution baselines
  const sweetwaterBaseline = coreData.sweetwater_baseline;
  const njBaseline = coreData.nj_baseline;
  const baselines = {
    state: {
      hours_short_per_resident_day: 0.15,
      pct_days_below_minimum: 20,
    },
    sweetwater: {
      hours_short_per_resident_day: sweetwaterBaseline?.understaffed_state_min_hprd_hrs_per_res_day ?? 0.08,
      pct_days_below_minimum: sweetwaterBaseline?.pct_days_below_cms_hprd ?? 12,
    },
  };

  // Header name: prefer enrollment DBA (from core data) over rollup facility_name
  const headerName = facility.enrollment_dba_name || ownershipData?.dba_name || ownershipData?.org_name || facility.facility_name;

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        {/* Back button outside the card */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(backUrl)}
          sx={{ mb: 1.5, ml: 1 }}
          size="small"
        >
          {backLabel}
        </Button>

        <Paper sx={{ width: '100%' }}>
          {/* Header Section */}
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              {/* Left: Name + Risk Score + chain selector */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h5" fontWeight={700}>
                    {headerName}
                  </Typography>
                  {facility.risk_score != null && (() => {
                    const score = Math.round(facility.risk_score);
                    const level = (facility.risk_level || 'moderate').toLowerCase();
                    const color = RISK_COLORS[level] || '#1976d2';
                    return (
                      <Tooltip title="Click to see risk score breakdown" arrow>
                        <Box
                          onClick={() => setRiskScoreOpen(true)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            cursor: 'pointer',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            backgroundColor: `${color}14`,
                            border: '1px solid',
                            borderColor: `${color}40`,
                            '&:hover': { backgroundColor: `${color}22` },
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              backgroundColor: color,
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              boxShadow: `0 2px 8px ${color}66`,
                            }}
                          >
                            {score}
                          </Box>
                          <Typography variant="caption" fontWeight={600} sx={{ color, textTransform: 'capitalize' }}>
                            {level} Risk
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })()}
                </Box>
                {facility.address && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {facility.address}
                  </Typography>
                )}

                {/* Facility selector for multi-facility chains */}
                {chainCcns && chainCcns.length > 1 && (
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <Typography variant="body2" color="text.secondary">
                      {chainName ? `${chainName} — ` : ''}Facility:
                    </Typography>
                    <Select
                      value={ccn}
                      onChange={(e) => handleFacilitySwitch(e.target.value as string)}
                      size="small"
                      sx={{ minWidth: 160 }}
                    >
                      {chainCcns.map((c: string) => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}
              </Box>

              {/* Right: Facility summary chips */}
              <Box display="flex" gap={1.5} alignItems="center" sx={{ mt: 0.5 }}>
                {facility.metadata?.city && (
                  <Chip
                    label={`${facility.metadata.city}, ${facility.state}`}
                    size="medium"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                  />
                )}
                {facility.metadata?.beds && (
                  <Chip
                    label={`${facility.metadata.beds} Beds`}
                    size="medium"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                  />
                )}
                {facility.metadata?.avg_census && (
                  <Chip
                    label={`Avg Census: ${Math.round(facility.metadata.avg_census)}`}
                    size="medium"
                    variant="outlined"
                    sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                  />
                )}
                <Chip
                  label="★ 2 CMS Rating"
                  size="medium"
                  variant="outlined"
                  sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#ed6c02', borderColor: '#ed6c02' }}
                />
              </Box>
            </Box>
          </Box>

          {/* Tabs */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}>
            <Tabs
              value={tabValue <= 3 ? tabValue : false}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 2,
                '& .MuiTabs-indicator': {
                  height: 3,
                },
                '& .MuiTab-root': {
                  minHeight: 56,
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  px: 3,
                  mx: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderRadius: '8px 8px 0 0',
                  },
                },
              }}
            >
              <Tab label="Understaffing" />
              <Tab label="Prior Conduct" />
              <Tab label="Related Parties" sx={{ '& .MuiTab-wrapper, &': { position: 'relative' }, '&::after': { content: '"beta"', position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)', fontSize: '0.5rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.6 } }} />
              <Tab label="Case Action" />
            </Tabs>
            <Box sx={{ flexGrow: 1 }} />
            <Tab
              label="Ownership"
              onClick={() => { handleTabChange(null as any, 4); }}
              sx={{
                minHeight: 56,
                fontSize: '0.95rem',
                fontWeight: 500,
                textTransform: 'none',
                px: 3,
                mx: 0.5,
                opacity: tabValue === 4 ? 1 : 0.7,
                color: tabValue === 4 ? 'primary.main' : 'text.secondary',
                borderBottom: tabValue === 4 ? '3px solid' : '3px solid transparent',
                borderColor: tabValue === 4 ? 'primary.main' : 'transparent',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: '8px 8px 0 0',
                },
              }}
            />
          </Box>

          {/* Tab Panels */}
          <Box sx={{ px: 3 }}>
            <TabPanel value={tabValue} index={0}>
              <UnderstaffingSummary
                facilityYears={facilityYears}
                facilityYearsHybrid={facilityYearsHybrid}
                facility={facility}
                state={facilityProp.state}
                stateContext={coreData.state_context}
                stateAcuityByYear={coreData.state_acuity_by_year}
                sweetwaterBaseline={coreData.sweetwater_baseline}
                njBaseline={coreData.nj_baseline}
                model={acuityModel}
                onModelChange={setAcuityModel}
              />

              <Box sx={{ mt: 4 }}>
                <AcuityGapCharts ccn={facilityProp.ccn} model={acuityModel} />
              </Box>

              {/* Staffing Trend card hidden for now (may remove fully later)
              <Box sx={{ mt: 4 }}>
                <StaffingTrendCard ccn={facilityProp.ccn} chowEvents={ownershipData?.chow_events} facilityAvgHprd={facilityProp.avg_pbj_nursing_hrd} />
              </Box>
              */}

            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <PriorConduct
                ccn={facilityProp.ccn}
                schemeId={schemeId || ''}
                facilityYears={facilityYears}
                state={facilityProp.state}
                beds={facilityProp.beds}
                facilityMetrics={{
                  total_harm_citations: facilityProp.total_harm_citations,
                  total_jeopardy_citations: facilityProp.total_jeopardy_citations,
                  harm_per_100_residents: facilityProp.harm_per_100_residents,
                  total_citations_per_100: facilityProp.total_citations_per_100,
                  total_citations: facilityProp.total_citations,
                  total_fine_amount: facilityProp.total_fine_amount,
                  staffing_ftags_total: facilityProp.staffing_ftags_total,
                  staffing_harm_total: facilityProp.staffing_harm_total,
                  total_staffing_ftags: facilityProp.total_staffing_ftags,
                  total_abuse_neglect_ftags: facilityProp.total_abuse_neglect_ftags,
                  total_quality_of_care_ftags: facilityProp.total_quality_of_care_ftags,
                  total_infection_control_ftags: facilityProp.total_infection_control_ftags,
                  total_pharmacy_ftags: facilityProp.total_pharmacy_ftags,
                  total_behavioral_health_ftags: facilityProp.total_behavioral_health_ftags,
                  total_dietary_ftags: facilityProp.total_dietary_ftags,
                }}
                citationBenchmarks={coreData.citation_benchmarks}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <RelatedPartiesSubTabs
                facility={facilityProp}
                facilityYears={facilityYears}
                rpLoading={rpLoading}
                rpData={rpData}
                onGenerateCostReports={handleGenerateCostReports}
                costReportGenerating={costReportGenerating}
                costReportProgress={costReportProgress}
                costReportStatus={costReportStatus}
                costReports={coreData.cost_reports}
                schemeId={schemeId || undefined}
                checkEngineSignals={checkEngineData?.signals}
                checkEngineYears={checkEngineData?.years}
                checkEngineLoading={checkEngineLoading || !checkEngineData}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <CaseActionSection schemeId={schemeId} ccn={ccn} />
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              {ownershipLoading && !ownershipData ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={32} />
                </Box>
              ) : ownershipData ? (
                <Box>
                  <Typography variant="h6" fontWeight={700} mb={2}>
                    Ownership
                  </Typography>

                  {/* Ownership History Timeline */}
                  {ownershipData.ownership_periods && ownershipData.ownership_periods.length > 0 && (
                    <Paper variant="outlined" sx={{ mb: 3, p: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle1" fontWeight={600} mb={1}>
                        Ownership History
                      </Typography>
                      <OwnershipTimeline
                        ownershipPeriods={ownershipData.ownership_periods}
                        chowEvents={ownershipData.chow_events}
                      />
                    </Paper>
                  )}

                  {/* Owners + Other Facilities side by side */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {ownershipData.owners.length > 0 && (
                      <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2, borderRadius: 2 }}>
                        <OwnershipSnapshot
                          owners={ownershipData.owners}
                          otherFacilities={[]}
                          facilityState={facilityProp.state}
                        />
                      </Paper>
                    )}
                    {(ownershipData.other_facilities.length > 0 || (ownershipData.owner_groups && ownershipData.owner_groups.length > 0)) && (
                      <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2, borderRadius: 2 }}>
                        <OwnershipSnapshot
                          owners={[]}
                          otherFacilities={ownershipData.other_facilities}
                          facilityState={facilityProp.state}
                          fromState={fromState}
                          ownerGroups={ownershipData.owner_groups}
                        />
                      </Paper>
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">No ownership data available.</Typography>
              )}
            </TabPanel>
          </Box>
        </Paper>

        {/* Risk Score Modal */}
        <Dialog
          open={riskScoreOpen}
          onClose={() => setRiskScoreOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Typography variant="h6" fontWeight={700}>Risk Score Breakdown</Typography>
            <IconButton onClick={() => setRiskScoreOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {(() => {
              const score = facility.risk_score;
              const level = (facility.risk_level || 'moderate').toLowerCase();
              const color = RISK_COLORS[level] || '#1976d2';
              const percentile = facility.state_percentile;

              return (
                <Box>
                  {/* Overall score */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, p: 2, backgroundColor: `${color}0A`, borderRadius: 2, border: '1px solid', borderColor: `${color}30` }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        backgroundColor: color,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '1.4rem',
                        flexShrink: 0,
                        boxShadow: `0 4px 12px ${color}44`,
                      }}
                    >
                      {score != null ? Math.round(score) : '-'}
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={700} sx={{ color, textTransform: 'capitalize' }}>
                        {level} Risk
                      </Typography>
                      {percentile != null && (
                        <Typography variant="body2" color="text.secondary">
                          {Math.round(percentile)}th percentile in {facility.state} (higher = more risk)
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Factor breakdown */}
                  {(() => {
                    // Parse top_risk_factors JSON to get raw values per factor
                    let rawByFactor: Record<number, string> = {};
                    try {
                      const parsed = typeof facility.top_risk_factors === 'string'
                        ? JSON.parse(facility.top_risk_factors)
                        : facility.top_risk_factors;
                      if (Array.isArray(parsed)) {
                        parsed.forEach((item: any) => {
                          if (item.factor != null && item.raw != null) {
                            rawByFactor[item.factor] = item.raw;
                          }
                        });
                      }
                    } catch { /* ignore parse errors */ }

                    return (
                      <>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                          Component Factors
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                          {RISK_FACTORS.map((factor, idx) => {
                            const factorScore = facility[factor.key];
                            const barValue = factorScore != null ? Math.min(factorScore, 100) : 0;
                            const barColor = barValue >= 75 ? '#d32f2f' : barValue >= 50 ? '#ed6c02' : barValue >= 25 ? '#fbc02d' : '#2e7d32';
                            const rawValue = rawByFactor[idx + 1];

                            return (
                              <Box key={factor.key}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                  <Box sx={{ flex: 1, mr: 2 }}>
                                    <Typography variant="body2" fontWeight={600}>
                                      {factor.label}
                                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        ({factor.weight}% weight)
                                      </Typography>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, mt: 0.25 }}>
                                      {factor.description}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                    <Typography variant="h6" fontWeight={700} sx={{ color: barColor, lineHeight: 1.2 }}>
                                      {factorScore != null ? Math.round(factorScore) : '-'}
                                    </Typography>
                                    {rawValue && (
                                      <Typography variant="caption" color="text.secondary">
                                        {rawValue}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={barValue}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: 'grey.200',
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 4,
                                      backgroundColor: barColor,
                                    },
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              );
            })()}
          </DialogContent>
        </Dialog>
      </Box>
    </Layout>
  );
}
